import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import { slateColors } from '../ContentPages/AppLandingPage/styles';

import ReliabilityScoreBadge from './ReliabilityScoreBadge';
import Price from './Price';
import { TimeIndication } from '../Elements/TimeComponents';
import LinesInfo from './LinesInfo';
import Tags from './Tags';
import { TaggedJourney } from '../../utils/mapJourneysData';

const Container = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`
  
const JourneyCardBody = styled('div')`
  background-color: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 2.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: ${slateColors[300]};
    transform: translateY(-2px);
  }
`

const Footer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 0.75rem;
  box-sizing: border-box;
  min-width: 0;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const PriceWrapper = styled('div')`
  margin-left: auto;
  
  @media (max-width: 640px) {
    margin-left: auto;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`

const LinesInfoWithMargin = styled.div`
  margin-bottom: 12px;
`;

type JourneyCardProps = {
  journey: TaggedJourney;
  onClick: () => void;
  /** Reference date from the journeys request (departure or arrival search param). Used for +1d time suffix. */
  referenceDate?: string | Date;
}

const JourneyCard = ({ journey, onClick, referenceDate }: JourneyCardProps) => {
  const navigate = useNavigate();

  const handleJourneyClick = () => {
    // Prepare data for Mixpanel event
    const journeyLegs = journey.legs.map((leg: any) => leg.line.name);
    const journeyPrice = journey.price ? `${journey.price.amount} ${journey.price.currency}` : "N/A";

    // Send Mixpanel event
    mixpanel.track('Journey Clicked', {
      journey_id: journey.id,
      number_of_legs: journey.legs.length,
      price: journeyPrice,
      line_names: journeyLegs,
    });

    navigate(`/journey/${journey.id}`, { state: { from: window.location.pathname, journey } });  // Pass current location in state
  };

  return (
    <Container>
      <JourneyCardBody onClick={handleJourneyClick}>
        {journey.tags && journey.tags.length > 0 && <Tags tags={journey.tags} />}
        <TimeIndication legs={journey.legs} journey={journey} referenceDate={referenceDate} />
        <LinesInfoWithMargin>
          <LinesInfo legs={journey.legs} />
        </LinesInfoWithMargin>
        <Footer>
          {journey.reliability?.reliabilityScore !== undefined && (
            <ReliabilityScoreBadge journey={journey} />
          )}
          {journey.price && journey.price.amount && (
            <PriceWrapper>
            <Price amount={journey.price.amount} currency={journey.price.currency} />
            </PriceWrapper>
          )}
        </Footer>
      </JourneyCardBody>
    </Container>
  );
};

export default JourneyCard;
